import { CARE_NECESSITIES, DOCS_FORM_NAMES } from "core/consts";
import { TransitionalCareFormData } from "core/types";
import { getUnixTime } from "date-fns";
import {
  ControlledCheckbox,
  ControlledDatePicker,
  ControlledInput,
  ControlledRadioGroup,
  ControlledSelect,
  ControlledTextarea,
} from "ds/ui";
import { TEXT_AREA_MAX_CHARACTERS } from "dsl/ecosystems/PatientForms/components";
import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { FormFieldConfigProps } from "..";
import { SourceWrapper } from "../../utils";

export const LegalRepresentativeConsentField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const { setValue } = useFormContext<TransitionalCareFormData>();
  const consentProvided = useWatch({
    name: "legal_representative_consent_provided",
  });

  useEffect(() => {
    if (!consentProvided) {
      setValue("legal_representative_last_name", "");
      setValue("legal_representative_first_name", "");
      setValue("legal_representative_location_street", "");
      setValue("legal_representative_location_street_number", "");
      setValue("legal_representative_location_zipcode", "");
      setValue("legal_representative_location_city", "");
    }
  }, [consentProvided, setValue]);

  return (
    <ControlledCheckbox
      label={
        translations.patientForms.transitionalCareForm.contactInformation
          .confirmedWithLegalRep
      }
      name="legal_representative_consent_provided"
    />
  );
};

export const LegalRepresentativeFirstNameField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const consentProvided = useWatch({
    name: "legal_representative_consent_provided",
  });

  return (
    <ControlledInput
      isDisabled={!consentProvided}
      label={
        translations.patientForms.transitionalCareForm.contactInformation
          .legalRepFirstName
      }
      name="legal_representative_first_name"
      placeholder=" "
    />
  );
};

export const LegalRepresentativeLastNameField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const consentProvided = useWatch({
    name: "legal_representative_consent_provided",
  });

  return (
    <ControlledInput
      isDisabled={!consentProvided}
      label={
        translations.patientForms.transitionalCareForm.contactInformation
          .legalRepName
      }
      name="legal_representative_last_name"
      placeholder=" "
    />
  );
};

export const LegalRepresentativeLocationStreetField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const consentProvided = useWatch({
    name: "legal_representative_consent_provided",
  });

  return (
    <ControlledInput
      isDisabled={!consentProvided}
      label={
        translations.patientForms.transitionalCareForm.contactInformation
          .legalRepStreet
      }
      name="legal_representative_location_street"
      placeholder=" "
    />
  );
};

export const LegalRepresentativeLocationStreetNumberField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const consentProvided = useWatch({
    name: "legal_representative_consent_provided",
  });

  return (
    <ControlledInput
      isDisabled={!consentProvided}
      label={
        translations.patientForms.transitionalCareForm.contactInformation
          .legalRepStreetNumber
      }
      name="legal_representative_location_street_number"
      placeholder=" "
    />
  );
};

export const LegalRepresentativeLocationZipcodeField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const consentProvided = useWatch({
    name: "legal_representative_consent_provided",
  });

  return (
    <ControlledInput
      isDisabled={!consentProvided}
      label={
        translations.patientForms.transitionalCareForm.contactInformation
          .legalRepZipCode
      }
      name="legal_representative_location_zipcode"
      placeholder=" "
    />
  );
};

export const LegalRepresentativeLocationCityField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const consentProvided = useWatch({
    name: "legal_representative_consent_provided",
  });

  return (
    <ControlledInput
      isDisabled={!consentProvided}
      label={
        translations.patientForms.transitionalCareForm.contactInformation
          .legalRepCity
      }
      name="legal_representative_location_city"
      placeholder=" "
    />
  );
};

export const PriorTreatmentEndDateField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const startDate = useWatch({ name: "prior_treatment_start_date" });

  return (
    <ControlledDatePicker
      label={
        translations.patientForms.transitionalCareForm.hospitalStay.releaseDate
      }
      maxValue={startDate ? undefined : getUnixTime(new Date())}
      minValue={startDate ?? undefined}
      name="prior_treatment_end_date"
    />
  );
};

export const TransitionalCareEndDateField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const startDate = useWatch({ name: "transitional_care_start_date" });

  return (
    <ControlledDatePicker
      label={
        translations.patientForms.transitionalCareForm.hospitalStay
          .endDateTransitionalCare
      }
      minValue={startDate ?? undefined}
      name="transitional_care_end_date"
    />
  );
};

export const PatientConfirmationField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const { setValue } = useFormContext<TransitionalCareFormData>();
  const patientConfirmation = useWatch({
    name: "transitional_care_patient_confirmation",
  });

  useEffect(() => {
    if (!patientConfirmation) {
      setValue("transitional_care_patient_confirmation_date", undefined);
    } else {
      setValue("transitional_care_patient_confirmation_explanation", "");
    }
  }, [patientConfirmation, setValue]);

  return (
    <ControlledCheckbox
      label={
        translations.patientForms.transitionalCareForm.sectionB
          .toggleConfirmedWithPatient
      }
      name="transitional_care_patient_confirmation"
    />
  );
};

export const PatientConfirmationDateField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const patientConfirmation = useWatch({
    name: "transitional_care_patient_confirmation",
  });

  if (!patientConfirmation) return null;

  return (
    <ControlledDatePicker
      label={
        translations.patientForms.transitionalCareForm.sectionB.dateFieldLabel
      }
      maxValue={getUnixTime(new Date())}
      name="transitional_care_patient_confirmation_date"
    />
  );
};

export const PatientConfirmationExplanationField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const patientConfirmation = useWatch({
    name: "transitional_care_patient_confirmation",
  });

  if (patientConfirmation) return null;

  return (
    <ControlledTextarea
      isRequired
      label={
        translations.patientForms.transitionalCareForm.sectionB
          .textFieldExplanationLabel
      }
      maxLength={TEXT_AREA_MAX_CHARACTERS}
      name="transitional_care_patient_confirmation_explanation"
      placeholder=" "
    />
  );
};

export const SuitableProvidersMoreThanTwentyField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const { setValue } = useFormContext<TransitionalCareFormData>();
  const moreThanTwenty = useWatch({
    name: "transitional_care_suitable_providers_more_than_twenty",
  });

  useEffect(() => {
    if (moreThanTwenty) {
      setValue("transitional_care_suitable_providers_count", undefined);
    }
  }, [moreThanTwenty, setValue]);

  return (
    <ControlledCheckbox
      label={
        translations.patientForms.transitionalCareForm.sectionB
          .toggleNumberOfProviders
      }
      name="transitional_care_suitable_providers_more_than_twenty"
    />
  );
};

export const SuitableProvidersCountField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const moreThanTwenty = useWatch({
    name: "transitional_care_suitable_providers_more_than_twenty",
  });

  if (moreThanTwenty) return null;

  const items = Array.from({ length: 21 }, (_, i) => ({
    id: i.toString(),
    label: i.toString(),
    value: i,
  }));

  return (
    <ControlledSelect
      items={items}
      label={
        translations.patientForms.transitionalCareForm.sectionB
          .dropdownNumberOfProviders
      }
      name="transitional_care_suitable_providers_count"
      renderItem={(item) => item.label}
      variant="outline"
    />
  );
};

export const AftercareApplicationSubmittedField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const { setValue } = useFormContext<TransitionalCareFormData>();
  const applicationSubmitted = useWatch({
    name: "aftercare_application_submitted",
  });

  useEffect(() => {
    if (!applicationSubmitted) {
      setValue("aftercare_application_date", undefined);
    } else {
      setValue("aftercare_application_explanation", "");
    }
  }, [applicationSubmitted, setValue]);

  return (
    <ControlledCheckbox
      label={
        translations.patientForms.transitionalCareForm.sectionC
          .applicationSubmitted
      }
      name="aftercare_application_submitted"
    />
  );
};

export const AftercareApplicationDateField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const applicationSubmitted = useWatch({
    name: "aftercare_application_submitted",
  });

  if (!applicationSubmitted) return null;

  return (
    <ControlledDatePicker
      label={
        translations.patientForms.transitionalCareForm.sectionC.applicationDate
      }
      maxValue={getUnixTime(new Date())}
      name="aftercare_application_date"
    />
  );
};

export const AftercareApplicationExplanationField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const applicationSubmitted = useWatch({
    name: "aftercare_application_submitted",
  });

  if (applicationSubmitted) return null;

  return (
    <ControlledTextarea
      isRequired
      label={
        translations.patientForms.transitionalCareForm.sectionC
          .textFieldDescription
      }
      maxLength={TEXT_AREA_MAX_CHARACTERS}
      name="aftercare_application_explanation"
      placeholder=" "
    />
  );
};

export const AftercareCareLevelAvailableField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const { setValue } = useFormContext<TransitionalCareFormData>();
  const careLevelAvailable = useWatch({
    name: "aftercare_care_level_available",
  });

  useEffect(() => {
    if (careLevelAvailable) {
      setValue("aftercare_care_level_applied", undefined);
      setValue("aftercare_care_level_explanation", "");
      setValue("aftercare_care_level_applied_date", undefined);
    }
  }, [careLevelAvailable, setValue]);

  return (
    <ControlledCheckbox
      label={translations.patientForms.transitionalCareForm.sectionC.careLevel}
      name="aftercare_care_level_available"
    />
  );
};

export const AftercareCareLevelAppliedField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const { setValue } = useFormContext<TransitionalCareFormData>();
  const careLevelAvailable = useWatch({
    name: "aftercare_care_level_available",
  });
  const careLevelApplied = useWatch({ name: "aftercare_care_level_applied" });

  useEffect(() => {
    if (!careLevelApplied) {
      setValue("aftercare_care_level_applied_date", undefined);
    } else {
      setValue("aftercare_care_level_explanation", "");
    }
  }, [careLevelApplied, setValue]);

  if (careLevelAvailable) return null;

  return (
    <ControlledCheckbox
      label={
        translations.patientForms.transitionalCareForm.sectionC.careLevelApplied
      }
      name="aftercare_care_level_applied"
    />
  );
};

export const AftercareCareLevelAppliedDateField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const careLevelAvailable = useWatch({
    name: "aftercare_care_level_available",
  });
  const careLevelApplied = useWatch({ name: "aftercare_care_level_applied" });

  if (careLevelAvailable || !careLevelApplied) return null;

  return (
    <ControlledDatePicker
      label={
        translations.patientForms.transitionalCareForm.sectionC
          .careLevelAppliedDate
      }
      maxValue={getUnixTime(new Date())}
      name="aftercare_care_level_applied_date"
    />
  );
};

export const AftercareCareLevelExplanationField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const careLevelAvailable = useWatch({
    name: "aftercare_care_level_available",
  });
  const careLevelApplied = useWatch({ name: "aftercare_care_level_applied" });

  if (careLevelAvailable || careLevelApplied) return null;

  return (
    <ControlledTextarea
      label={
        translations.patientForms.transitionalCareForm.sectionC.textFieldLabel
      }
      maxLength={TEXT_AREA_MAX_CHARACTERS}
      name="aftercare_care_level_explanation"
      placeholder=" "
    />
  );
};

export const CareNecessityField = ({
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const { setValue } = useFormContext<TransitionalCareFormData>();
  const careNecessity = useWatch({ name: "care_necessity" });

  useEffect(() => {
    if (careNecessity !== CARE_NECESSITIES.FULL_STOP_OUTSIDE_ENTITLEMENT) {
      setValue("aftercare_start_date", undefined);
    }
  }, [careNecessity, setValue]);

  const options = [
    {
      label: translations.patientForms.transitionalCareForm.sectionF.bulletOne,
      value: CARE_NECESSITIES.AFTER_CARE_ENSURED_DURING,
    },
    {
      label: translations.patientForms.transitionalCareForm.sectionF.bulletTwo,
      value: CARE_NECESSITIES.AFTER_CARE_COULD_BE_ENSURED,
    },
    {
      label:
        translations.patientForms.transitionalCareForm.sectionF.bulletThree,
      subForm: () => (
        <SourceWrapper<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>
          fieldName="aftercare_start_date"
          formName={formName}
          isIconOnly
          sourceInformation={sourceInformation}
        >
          <ControlledDatePicker
            label={
              translations.patientForms.transitionalCareForm.sectionF
                .dateFieldLabel
            }
            name="aftercare_start_date"
          />
        </SourceWrapper>
      ),
      value: CARE_NECESSITIES.FULL_STOP_OUTSIDE_ENTITLEMENT,
    },
    {
      label: translations.patientForms.transitionalCareForm.sectionF.bulletFour,
      value: CARE_NECESSITIES.NO_FURTHER_NEED,
    },
  ];

  return (
    <ControlledRadioGroup
      aria-label={translations.patientForms.transitionalCareForm.sectionF.title}
      name="care_necessity"
      options={options}
      size="md"
    />
  );
};
